<script lang="ts" generics="T, R">
	import type { EventDetail } from "@splidejs/svelte-splide/types/events";
	import type { Cache } from "$lib/serv/core/cache";
	import type { Snippet } from "svelte";
	
	import { Splide, type SplideCore, type Options } from '$lib/serv/vendor-integration/splide';
	import { GAP, MIN_BANNER_WIDTH, MIN_PRODUCT_WIDTH } from "$lib/serv/layout";
	import { debounceTime, type Observable, type Subscription } from "rxjs";
	import { widthFromResizeObserver } from "$lib/util/obs";

	import '@splidejs/svelte-splide/css/core';

	type Props = {
		cache: Cache<T>;
		type?: any;
		key?: (payload: T) => R[];
		uniqueKey?: ((item: R) => string|number);
		options?: Options;
		resize?: () => void;
		carousel?: any,
		id?: string;
		title?: Snippet;
		children: Snippet<[{
			item: R;
			index: number;
		}]>;
	}

	let {
		cache,
		type = 'aisle',
		key = (payload: T) => (Array.isArray(payload) ? payload : Array(9)) as R[],
		uniqueKey,
		options = {},
		id,
		title,
		children,
	}: Props = $props();

	let loading = $state(true);
	let columns = 3;

	// const _options: Options = {
	// 	...(noAnimationOnDesktop && {
	// 	// 	breakpoints: {
	// 	// 		1024: {
	// 	// 		},
	// 	// 	},
	// 		autoWidth: false,
	// 		perPage: 3,
	// 		drag: false,
	// 		speed: 0,
	// 	}),
	// 	width: '100%',
	// 	autoWidth: true,
	// 	pagination: false,
	// 	perPage: 3,
	// 	gap: GAP,
	// 	mediaQuery: 'min',
	// 	...options,
	// 	...(noAnimationOnDesktop && {
	// 		autoWidth: false,
	// 		perPage: 3,
	// 		drag: false,
	// 		speed: 0,
	// 		breakpoints: {
	// 			1024: {
	// 				autoWidth: true,
	// 				perPage: 1,
	// 				drag: true,
	// 				speed: 0.5,
	// 			},
	// 		},
	// 	}),
	// };

	let carousel: {
		splide?: SplideCore;
		options: Options;
	} = $state({
		options: {},
	});

	let carouselWidth$: Observable<number>;
	let carouselWidthSubscriber: Subscription;

	const _options: Options = {
		width: '100%',
		autoWidth: true,
		pagination: false,
		perPage: 1,
		gap: GAP,
		mediaQuery: 'min',
		...((type === 'aisle' || type === 'swimlane' || type.startsWith('banners')) && {
			breakpoints: {
				1024: {
					autoWidth: false,
					perPage: 3,
					drag: false,
					speed: 0,
				},
			},
		}),
		...options,
	};

	function setOnWidthChange(width: number) {

		switch (type) {
			case 'aisle':
				columns = Math.floor((width + GAP) / (MIN_PRODUCT_WIDTH + GAP));
				break;
			case 'swimlane':
			case 'banners small':
			case 'banners large':
			case 'banners deluxe':
				columns = Math.floor((width + GAP) / (MIN_BANNER_WIDTH + GAP));
				break;
			default:
				columns = 1;
		}
		
		if (matchMedia('(min-width: 1024px)').matches) {
			carousel.options = {
				perPage: columns,
			};
		} else {
			carousel.splide?.refresh();
		}

		loading = false;
	};

	function mounted(e: CustomEvent<EventDetail<Record<string, unknown>>>) {
		carouselWidth$ = widthFromResizeObserver(e.detail.splide.root).pipe(
			debounceTime(300),
		);
		carouselWidthSubscriber = carouselWidth$.subscribe(setOnWidthChange);
	}

	function destroy() {
		carouselWidthSubscriber?.unsubscribe();
	}
</script>

<div class="m-v-l relative" class:loading data-testid={id}>

	{#if cache && (cache.loading || cache.error || key(cache.payload as T)?.length)}
		{@const { error, payload } = cache}
		{@const items = key(payload as T)}
		{#if error}
			<div class="message error body-m book">
				<p class="body-m book">
					{error?.friendly_message ?? error ?? "Unable to load grid."}
				</p>
			</div>
		{:else if items?.length}

			<Splide.Carousel bind:this={carousel} hasTrack={false} options={_options} class="carousel {type}" on:mounted={mounted} on:destroy={destroy}>

				<div class="title flex mobile m-small small-gap flex-nowrap">
					{#if title}
						{@render title()}
					{/if}
					<div class="splide__arrows desktop-only button-group align-end text-right">
						<button class="button-circle splide__arrow splide__arrow--prev"><span class="icon-chevron-left-28"></span></button>
						<button class="button-circle splide__arrow splide__arrow--next"><span class="icon-chevron-right-28"></span></button>
					</div>
				</div>
				<Splide.Track>
					{#each items as item, index (uniqueKey ? uniqueKey(item) : index)}
						<Splide.Slide>
							{@render children({ item, index })}
						</Splide.Slide>
					{/each}
				</Splide.Track>
			</Splide.Carousel>
			
		{/if}
		<div class="carousel loading splide__track {type}">
			{#each Array(9) as _}
				<div class="item">
					<div class="image" />
					<div class="text" />
				</div>
			{/each}
		</div>
	{/if}
</div>